import { AW_LOCALIZATION_API_URL, EnvType, getLocalizedLabels, HeaderRequestId } from '@blk/aw-api-clients';
import { AvailableLanguage } from '@blk/aw-bff-typescript-models';
import { EventType, LogAction, Logger } from '@blk/aw-logger';
import { translateDynamicContent } from '@blk/aw-utils-core';
import * as ApiTypes from '@blk/awt-localization';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { createInstance, FormatFunction, i18n as i18nType, InitOptions, TFunction } from 'i18next';
import LanguageDetector, { CustomDetector } from 'i18next-browser-languagedetector';
import Backend, { HttpBackendOptions } from 'i18next-http-backend';
import { produce, setAutoFreeze } from 'immer';
import { capitalize, get, isNil, isString, isUndefined, set } from 'lodash';
import memoizeOne from 'memoize-one';
import { initReactI18next } from 'react-i18next';
import { isColGroupDef } from './GridUtils';
import {
  AW_URL_QUERY_PARAM,
  getAwUrlParam,
  getConfigurationDirectoryFromUrl,
  getEnvironmentType,
  getUrlParam,
} from './UrlUtils';

// Extend Window interface to include i18n property for ADS components
declare global {
  interface Window {
    i18n?: i18nType;
  }
}

/**
 * These are the only keys that can be translated on initialization if they are present in the config
 *
 * TODO: Items that will be overlooked with this system:
 * * snooze buckets colTag -- This property should not be named colTag as it is a title
 */
const translatableKeys: { [key: string]: boolean } = Object.freeze({
  // General keys
  title: true,
  displayText: true,
  text: true,
  name: true,
  description: true,
  searchPlaceholderText: true,
  label: true,
  tooltipMessage: true,
  sectorsLabel: true,

  // First Column Header Name
  firstColumnHeaderName: true,

  // Filter panel group
  groupTitle: true,

  // Risk Monitor
  aggregateText: true,
  aggregateDisplayText: true,
  accountLookupPlaceholderText: true,
  xAxisTitle: true,
  yAxisTitle: true,
  xAxisLabel: true,
  yAxisLabel: true,
  middleRangeLabel: true,
  benchmarkProjectionLabel: true,
  titlePrefix: true,

  // My book toggle
  allAccountsLabel: true,
  myBookLabel: true,

  // complResultMap
  FAILED: true,
  PASSED: true,

  // Alerts
  itemsTitle: true,

  // PB
  tabName: true,

  // Print
  buttonDisplayText: true,
  pageName: true,
  displayName: true,
  header: true,
  confirmButtonText: true,
  message: true,
  pageTitle: true,

  // Exhibit related
  exhibitTitle: true,
  exhibitDescription: true,
  lowDisplayText: true,
  highDisplayText: true,
  disclaimer: true,

  // Entities or other strings which have singular/plural nouns
  singular: true,
  plural: true,

  groupName: true,
  group: true,

  // appNavigation
  overflowLabel: true,

  menuLabel: true,
  menuSuffix: true,

  // Aggregate Views - WorkflowStep radio options
  selectionInstructions: true,
  toggleGroupInstructions: true,
});

const translatableObjectKeys: { [key: string]: boolean } = Object.freeze({
  colorPaletteDefs: true,
});

/**
 * Translation data type used for caching and i18next backend.
 *
 * The localization API originally returns an array of SkinnyAwLocalization objects:
 * [{ elementId: "welcome.title", translation: "Welcome" }, { elementId: "nav.home", translation: "Home" }]
 *
 * The parse function in generateI18nInitOptions transforms this into a flat object for i18next:
 * { "welcome.title": "Welcome", "nav.home": "Home" }
 *
 * This structure allows for fast O(1) key-value lookups during translation.
 */
type TranslationData = { [elementId: string]: string };

/* Type guards */
function isTranslationData(data: unknown): data is TranslationData {
  return (
    typeof data === 'object' &&
    data !== null &&
    !Array.isArray(data) &&
    Object.values(data).every((value) => typeof value === 'string')
  );
}

function isError(err: unknown): err is Error {
  return err instanceof Error;
}

/**
 * Custom i18next backend that adds session-based caching to the HTTP backend.
 * Caches translation data in memory to avoid duplicate API calls during the same session.
 * Uses static cache to share data between multiple i18n instances (i18nInstance and i18nReportInstance).
 */
export class SessionCachedHttpBackend extends Backend {
  // Use static cache to share between instances
  private static sessionCache = new Map<string, TranslationData>();

  read(language: string, namespace: string, callback: (err: Error | null, data?: TranslationData) => void): void {
    // Create comprehensive cache key including URL parameters
    const pkgFromUrl = getAwUrlParam(AW_URL_QUERY_PARAM.PKG);
    const configurationDirectoryFromUrl = getConfigurationDirectoryFromUrl();
    const cacheKey = `${language}-${namespace}-${pkgFromUrl || 'default'}-${configurationDirectoryFromUrl || 'default'}`;

    // Try session cache first
    const cachedData = SessionCachedHttpBackend.sessionCache.get(cacheKey);
    if (cachedData) {
      callback(null, cachedData);
      return;
    }

    // Fallback to HTTP backend
    super.read(language, namespace, (httpErr: unknown, httpData: unknown) => {
      if (!httpErr && isTranslationData(httpData)) {
        // Save to static session cache shared between instances
        SessionCachedHttpBackend.sessionCache.set(cacheKey, httpData);
        callback(null, httpData);
      } else {
        let error: Error | null = null;
        if (isError(httpErr)) {
          error = httpErr;
        } else if (httpErr) {
          const errorMessage = JSON.stringify(httpErr);
          error = new Error(errorMessage);
        }
        callback(error, undefined);
      }
    });
  }
}

/* i18n Instances */
export const i18nInstance: i18nType = createInstance().use(SessionCachedHttpBackend).use(initReactI18next);
export const i18nReportInstance: i18nType = createInstance().use(SessionCachedHttpBackend).use(initReactI18next);

/**
 * Special namespace for ADS components that bypasses the ADS i18n system.
 * When this namespace is used, ADS components will accept pre-translated values directly.
 */
export const ADS_BYPASS_NAMESPACE = 'awt-fe';

/**
 * Create a no-op i18n instance on window.i18n to enable ADS components' i18n mode.
 * ADS components check for window.i18n to determine if i18n mode is active - without this,
 * i18n mode would not take effect and ADS components would not support localization.
 */
if (typeof window !== 'undefined') {
  const noOpI18nInstance = createInstance();

  // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget init for no-op ADS i18n stub
  noOpI18nInstance.init({
    lng: 'en',
    resources: {},
    nsSeparator: false,
    keySeparator: false,
  });

  // Override the 'on' method to emit 'loaded' immediately when components register listeners
  const originalOn = noOpI18nInstance.on?.bind(noOpI18nInstance);
  noOpI18nInstance.on = (event: string, callback: (...args: any[]) => void) => {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression -- optional chain on event emitter returns void
    const result = originalOn?.(event, callback);
    // Immediately trigger 'loaded' event for any component that registers a listener
    if (event === 'loaded') {
      callback();
    }
    return result;
  };

  window.i18n = noOpI18nInstance;
  localStorage.setItem('i18n', 'true');
}

export const enableDebugLocalizations = () => {
  const debugLocalizations = getUrlParam('debug') === 'localizations';
  const envType = getEnvironmentType(window.location.pathname);
  // if url contains debug=localizations, then show the key instead of the translation
  if (debugLocalizations && (envType === EnvType.BETA || envType === EnvType.UAT)) {
    Logger.trackEvent({
      action: LogAction.DEBUG_LOCALIZATIONS,
      elementInfo: { eventType: EventType.EVENT_LOAD },
    });
    i18nInstance.t = ((key: string) => {
      const prefix = '{';
      const modifiedKey = key?.startsWith(prefix) ? key : `${prefix}${key}}`;
      return modifiedKey;
    }) as TFunction;
  }
};

/**
 * Purpose: Used during app initialization to take any config properties that could be a localization tag and translate them
 * Implementation: This function walks the tree of an object, replacing anything that looks like a localization field with a relevant,
 * available translation, if one exists. A localization field is in the form of "prefix_description", using only letters
 * and numbers. Recursively translates object keys when a parent object is in translatableObjectKeys.
 * @param obj -- The tree currently being inspected for translation
 * @param languageTag -- The language tag to translate the fields to, this specifies the language to use for translation (required)
 * @param keyFrom -- The key of the property the previous level was from
 * @param sourceKeyFrom -- The original translatable source key that enables recursive key translation
 */
export const translateRelevantLocalizationFields = <T>(
  obj: T,
  languageTag?: string,
  keyFrom?: string,
  sourceKeyFrom?: string
): T => {
  const safeLng = languageTag || i18nInstance.language;
  const i18nToUse =
    safeLng === i18nReportInstance.language && i18nReportInstance.language !== i18nInstance.language
      ? i18nReportInstance
      : i18nInstance;
  if (!obj) {
    return obj;
  } else if (typeof obj === 'string' && keyFrom && translatableKeys[keyFrom]) {
    return i18nToUse.t(obj, { skipInterpolation: true, lng: safeLng }) as T;
  } else if (Array.isArray(obj)) {
    return obj.map((item) =>
      translateRelevantLocalizationFields(item as unknown, safeLng, keyFrom, sourceKeyFrom)
    ) as T;
  } else if (typeof obj === 'object') {
    // Check if current keyFrom or sourceKeyFrom makes this object translatable
    const shouldTranslateKeys =
      (!isUndefined(sourceKeyFrom) && sourceKeyFrom in translatableObjectKeys) ||
      (!isUndefined(keyFrom) && keyFrom in translatableObjectKeys);

    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        // Translate nested keys only if current object or any parent is translatable, otherwise preserve keys
        shouldTranslateKeys ? i18nToUse.t(key, { skipInterpolation: true, lng: safeLng }) : key,
        translateRelevantLocalizationFields(
          value,
          safeLng,
          key,
          // Determine the source key to pass down - preserve original sourceKey or use current if translatable
          keyFrom && translatableObjectKeys[keyFrom] ? keyFrom : sourceKeyFrom
        ),
      ])
    ) as T;
  }
  return obj;
};

export const memoizedTranslateRelevantLocalizationFields = memoizeOne(
  <T>(obj: T, languageTag: string): T => translateRelevantLocalizationFields(obj, languageTag)
);

/**
 * Translate all the values in a given object.
 * Automatically uses i18nReportInstance when report language differs from app language.
 * @param obj -- The tree currently being inspected for translation
 */
export const translateAllValues = <T>(obj: T): T => {
  if (!obj) {
    return obj;
  }
  const i18nToUse =
    i18nReportInstance.language && i18nReportInstance.language !== i18nInstance.language
      ? i18nReportInstance
      : i18nInstance;
  if (typeof obj === 'string' && i18nToUse.exists(obj)) {
    return i18nToUse.t(obj, { skipInterpolation: true }) as T;
  }
  if (Array.isArray(obj)) {
    return obj.map(translateAllValues) as T;
  }
  if (typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, translateAllValues(value)])) as T;
  }
  return obj;
};

// TODO: Migrate all instances of translateRowData to this function
export const translateExhibitData = <T extends object>(
  rowData: T[] | undefined,
  columns: string[] | undefined,
  t: TFunction,
  prefix: string = ''
): T[] => {
  if (isNil(rowData)) {
    return [];
  } else if (isNil(columns)) {
    return rowData;
  }

  return rowData.map((row) => {
    let clonedRow = { ...row };
    columns?.forEach((fieldPath) => {
      const fieldVal = get(row, fieldPath);

      const translatedFieldVal = translateDynamicContent(fieldPath ? prefix + fieldPath : fieldPath, fieldVal, t);
      if (translatedFieldVal !== fieldVal) {
        /**
         * onCellValueChanged is used in some ag-grid tables which is directly mutating the data. onCellValueChanged should not
         * be used in immutable mode. produce by default freezes the object but it was causing errors in tables that are editable e.g. HoldingsTable
         * We are setting autoFreeze to false globally before creation of row data and setting autoFreeze back to true afterwards.
         * The autoFreeze setter should only be removed after we have deprecated all usage of onCellValueChanged
         */
        setAutoFreeze(false);
        clonedRow = produce(clonedRow, (draft) => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- this is a bug in the eslint rule
          set(draft, fieldPath, translatedFieldVal);
        });
        setAutoFreeze(true);
      }
    });
    return clonedRow;
  });
};

export const translateRowData = <T extends object>(
  rowData: T[] | undefined,
  colDefs: (ColGroupDef | ColDef)[] | undefined,
  t: TFunction,
  prefix: string = ''
): T[] => {
  const colDefFields = colDefs?.reduce((acc: string[], currentColDef) => {
    if (isColGroupDef(currentColDef)) {
      // retrieve field from the children if colDef is a column group (i.e. ColGroupDef type)
      currentColDef.children.forEach((colDef: ColDef) => {
        if (colDef.field) {
          acc.push(colDef.field);
        }
      });
    } else {
      // retrieve field directly if colDef a single column (i.e. ColDef type)
      if (currentColDef.field) {
        acc.push(currentColDef.field);
      }
    }
    return acc;
  }, []);
  return translateExhibitData(rowData, colDefFields, t, prefix);
};

const languageDetectorOrder = ['querystring', 'localStorage', 'navigator'];

const languageDetectorOptions = {
  order: languageDetectorOrder,
  lookupQuerystring: 'lang',
  lookupLocalStorage: 'i18nextLng',
};

const detectLanguage = (): string | undefined => {
  const detector = new LanguageDetector(languageDetectorOptions);
  const detectionResult = languageDetectorOrder
    .map((detectorName) => (detector.detectors[detectorName] as CustomDetector).lookup(languageDetectorOptions))
    .find((result) => !!result);
  const rawResult = Array.isArray(detectionResult) ? detectionResult[0] : detectionResult;

  // Normalize to lowercase to handle case-insensitive lang parameters (e.g., DE -> de, ES -> es)
  return rawResult ? rawResult.toLowerCase() : rawResult;
};

export const findBestFitLanguage = (
  lng: string | undefined,
  availableLanguages: AvailableLanguage[]
): AvailableLanguage => {
  // First, check if availableLanguages has only one entry, if so, return that
  if (availableLanguages.length === 1) {
    return availableLanguages[0];
  }

  // Second, find exact match (case-insensitive to handle BCP-47 mixed casing e.g. zh-Hant vs zh-hant)
  const exactMatch = availableLanguages.find((al) => al.languageTag.toLowerCase() === lng?.toLowerCase());
  if (exactMatch) {
    return exactMatch;
  }

  // Third, try a match on just the language code (e.g. if we have 'en-US', just search for 'en')
  const languageCode = lng?.slice().replace(/-(.*)/g, '');
  const languageCodeMatch = availableLanguages.find(
    (al) => al.languageTag.toLowerCase() === languageCode?.toLowerCase()
  );
  if (languageCodeMatch) {
    return languageCodeMatch;
  }

  const english = availableLanguages.find((al) => al.languageTag === 'en');

  // Lastly, we must have rendered english at this point, so put english in the dropdown
  if (english) {
    return english;
  }
  // If nothing matches, give up. The fallback is not translated because we wouldn't know what language to translate it into
  throw Error("Unable to fall back to english as your language as english ('en') is not present in availableLanguages");
};

const formatValue: FormatFunction = (value, format) => {
  if (isUndefined(format) || !isString(value)) {
    console.error('Invalid format or value passed to formatValue');
    return value as string;
  }

  switch (format) {
    case 'lowercase':
      return value.toLowerCase();
    case 'uppercase':
      return value.toUpperCase();
    case 'capitalize':
      return capitalize(value);
    //More formatter if needed
    default:
      return value;
  }
};

/**
 * Recursively processes the fields of an object, applying a transformation function to specific fields.
 * @param inputObject - The object to process.
 * @param interpolateFn - The interpolation function.
 * @returns A new object with transformed fields.
 */
export const interpolateDeepObjectFields = (
  inputObject: unknown,
  interpolateFn: (fieldValue: string) => string
): unknown => {
  if (typeof inputObject !== 'object' || Array.isArray(inputObject) || inputObject === null) {
    // Return non-object values (e.g., strings, numbers, arrays, null) as-is
    return inputObject;
  }

  return Object.entries(inputObject).reduce<Record<string, unknown>>((processedObject, [fieldKey, fieldValue]) => {
    if (fieldKey === 'content' && typeof fieldValue === 'string') {
      // Apply the interpolate function to the `content` field if it is a string
      processedObject[fieldKey] = interpolateFn(fieldValue);
    } else if (typeof fieldValue === 'object' && fieldValue !== null) {
      // Recursively process nested objects
      processedObject[fieldKey] = interpolateDeepObjectFields(fieldValue, interpolateFn);
    } else {
      // Keep other fields unchanged
      processedObject[fieldKey] = fieldValue;
    }
    return processedObject;
  }, {});
};

export const generateI18nInitOptions = (
  availableLanguages: AvailableLanguage[],
  headerRequestId: HeaderRequestId,
  isAGraphEnabled: boolean = false
): InitOptions & { backend: HttpBackendOptions } => {
  const bestFitLanguage = findBestFitLanguage(detectLanguage(), availableLanguages);
  const pkgFromUrl = getAwUrlParam(AW_URL_QUERY_PARAM.PKG);
  const configurationDirectoryFromUrl = getConfigurationDirectoryFromUrl();

  if (isAGraphEnabled) {
    // Use a custom backend loader function that will be called for each language
    return {
      lng: bestFitLanguage.languageTag,
      backend: {
        loadPath: '{{lng}}', // actual loading happens in custom request
        request: (
          _options: unknown,
          url: string,
          _payload: unknown,
          callback: (error: Error | null, data?: { status: number; data: string }) => void
        ) => {
          const locale = url; // url will be the language code
          getLocalizedLabels(locale, pkgFromUrl)
            .then((translations) => {
              // Return the translations for this specific language
              callback(null, {
                status: 200,
                data: JSON.stringify(translations[locale] || {}),
              });
            })
            .catch((error) => {
              callback(error as Error);
            });
        },
      } as HttpBackendOptions,
      interpolation: {
        escapeValue: false,
        skipOnVariables: false,
        format: formatValue,
      },
      fallbackLng: false,
      nsSeparator: false,
      keySeparator: false,
      load: 'currentOnly',
      compatibilityJSON: 'v3',
      partialBundledLanguages: true, // required to enable additional namespaces for content
      // the function's typings expect `value` to be a string, but it's an object if we're using the `returnedObjectHandler`
      returnedObjectHandler: function (_key, value: unknown, options: object) {
        // Interpolation doesn't work on nested objects - so we need to do it manually
        return interpolateDeepObjectFields(value, (content) =>
          i18nInstance.services.interpolator.interpolate(content, options, detectLanguage() || 'en', options)
        );
      },
    };
  }
  // Construct loadPath using pkgFromUrl and configurationDirectoryFromUrl if present
  let loadPath = `${AW_LOCALIZATION_API_URL}/fields/language/{{lng}}`;
  const queryParams = new URLSearchParams();
  if (configurationDirectoryFromUrl) {
    queryParams.append('configurationDirectory', configurationDirectoryFromUrl);
  }
  if (pkgFromUrl) {
    queryParams.append('pkg', pkgFromUrl);
  }
  if (queryParams.toString().length > 0) {
    loadPath += `?${queryParams.toString()}`;
  }

  return {
    lng: bestFitLanguage.languageTag,
    backend: {
      crossDomain: true,
      loadPath,
      parse: (data: string) =>
        (JSON.parse(data) as ApiTypes.SkinnyAwLocalization[]).reduce<{ [elementId: string]: string }>(
          (prev, current) => {
            prev[current.elementId] = current.translation;
            return prev;
          },
          {}
        ),
      customHeaders: headerRequestId.toHeader(),
    },
    interpolation: {
      escapeValue: false,
      /*
       * Defaults to true (earlier was false) from i18next v21.0.0 onwards,
       * Needs to be false to avoid printing interpolation markers e.g. {{key}} when the value for the key is not provided
       */
      skipOnVariables: false,
      format: formatValue,
    },
    fallbackLng: false,
    nsSeparator: false,
    keySeparator: false,
    load: 'currentOnly',
    compatibilityJSON: 'v3',
    partialBundledLanguages: true, // required to enable additional namespaces for content
    // the function's typings expect `value` to be a string, but it's an object if we're using the `returnedObjectHandler`
    returnedObjectHandler: function (_key, value: unknown, options: object) {
      // Interpolation doesn't work on nested objects - so we need to do it manually
      return interpolateDeepObjectFields(value, (content) =>
        i18nInstance.services.interpolator.interpolate(content, options, detectLanguage() || 'en', options)
      );
    },
  };
};

export const changeLanguageWithLocalStorage = async (lng: string) => {
  localStorage.setItem('i18nextLng', lng);
  await i18nReportInstance.changeLanguage(lng);
  await i18nInstance.changeLanguage(lng);
};

export const initializeI18n = async (availableLanguages?: AvailableLanguage[], isAGraphEnabled?: boolean) => {
  if (!availableLanguages || availableLanguages.length === 0) {
    throw Error('availableLanguages is missing or empty. Please add availableLanguages to your config');
  }
  const headerRequestId = new HeaderRequestId();
  await i18nInstance.init(generateI18nInitOptions(availableLanguages, headerRequestId, isAGraphEnabled), (error) => {
    if (error) {
      throw Error(`Unable to load translations! \nRequest ID: ${headerRequestId.getId()}\n, ${error}`);
    }
  });
};

export const initializeI18nReportInstance = async (
  availableLanguages?: AvailableLanguage[],
  isAGraphEnabled?: boolean
) => {
  if (!availableLanguages || availableLanguages.length === 0) {
    throw Error('availableLanguages is missing or empty. Please add availableLanguages to your config');
  }
  const headerRequestId = new HeaderRequestId();
  await i18nReportInstance.init(
    generateI18nInitOptions(availableLanguages, headerRequestId, isAGraphEnabled),
    (error) => {
      if (error) {
        throw Error(`Unable to load translations! \nRequest ID: ${headerRequestId.getId()}\n, ${error}`);
      }
    }
  );
};

export const changeReportLanguage = (languageTag: string): Promise<TFunction> =>
  i18nReportInstance.changeLanguage(languageTag);

//Intl.getCanonicalLocales() throws error if the input string is not a valid BCP47 language tag which is what ISO language tags are based on
export const isValidISOLanguageTag = (tag: string): boolean => {
  try {
    Intl.getCanonicalLocales(tag);
    /**
     * Regex to validate ISO language tags in BCP 47-like format.
     *
     * Structure:
     * - Language: ISO 639 (2–3 lowercase letters)
     * - Optional Script: ISO 15924 (1 uppercase + 3 lowercase letters)
     * - Optional Region: ISO 3166-1 alpha-2 (2 uppercase letters)
     *
     * Examples of valid tags:
     * - "en"         → Language only
     * - "en-US"      → Language + Region
     * - "zh-Hant"    → Language + Script
     * - "fr-CA"      → Language + Region
     *
     * Invalid examples:
     * - "en-us"      → Region must be uppercase
     * - "EN_US"      → Underscore not allowed
     */
    const isoTagRegex = /^[a-z]{2,3}(-[A-Z][a-z]{3})?(-[A-Z]{2})?$/;
    return isoTagRegex.test(tag);
  } catch {
    return false;
  }
};
